import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { csAdminApi } from '../services/api';
import {
  getActiveAuthStorage,
  clearCsadminAuthStorage,
  migrateLegacyAuthStorage,
} from '../constants/authStorage';
import { normalizeCsAdminRole } from '../constants/csAdminRoles';
import {
  userBelongsOnCurrentPortal,
  WRONG_PORTAL_LOGIN_MESSAGE,
} from '../utils/portalAccess';

function normalizeUserForSession(userData) {
  if (!userData || typeof userData !== 'object') return userData;
  const role = normalizeCsAdminRole(userData.role);
  if (!role || role === userData.role) return userData;
  return { ...userData, role };
}

const AuthContext = createContext(null);

function readStoredSession() {
  migrateLegacyAuthStorage();
  const storage = getActiveAuthStorage();
  const token = localStorage.getItem(storage.token);
  const savedUser = localStorage.getItem(storage.user);
  if (!token || !savedUser) return null;
  try {
    const user = normalizeUserForSession(JSON.parse(savedUser));
    if (!userBelongsOnCurrentPortal(user?.role)) {
      return null;
    }
    return { token, user };
  } catch (error) {
    console.error('Error parsing saved user:', error);
    clearCsadminAuthStorage();
    return null;
  }
}

function persistSession({ token, refreshToken, user }) {
  const storage = getActiveAuthStorage();
  const normalized = normalizeUserForSession(user);
  localStorage.setItem(storage.token, token);
  if (refreshToken) {
    localStorage.setItem(storage.refreshToken, refreshToken);
  }
  localStorage.setItem(storage.user, JSON.stringify(normalized));
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const applySession = useCallback((session) => {
    if (!session) {
      setUser(null);
      setIsAuthenticated(false);
      return;
    }
    setUser(normalizeUserForSession(session.user));
    setIsAuthenticated(true);
  }, []);

  const hydrateFromStorage = useCallback(() => {
    const session = readStoredSession();
    if (!session) {
      applySession(null);
      return;
    }
    applySession(session);
  }, [applySession]);

  useEffect(() => {
    hydrateFromStorage();
    setLoading(false);

    const storage = getActiveAuthStorage();
    const watchedKeys = [
      ...Object.values(storage),
    ];
    const onStorage = (event) => {
      if (!event.key || !watchedKeys.includes(event.key)) return;
      hydrateFromStorage();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [hydrateFromStorage]);

  const rejectWrongPortal = (userData) => {
    if (userBelongsOnCurrentPortal(userData?.role)) {
      return null;
    }
    clearCsadminAuthStorage();
    applySession(null);
    return WRONG_PORTAL_LOGIN_MESSAGE;
  };

  const login = async (credentials) => {
    try {
      const response = await csAdminApi.login({
        username: (credentials.username || '').trim(),
        password: (credentials.password || '').trim(),
      });
      const body = response.data;

      if (body.requiresOtp) {
        return {
          success: false,
          requiresOtp: true,
          otpSessionId: body.otpSessionId,
          maskedEmail: body.maskedEmail,
          expiresInSeconds: body.expiresInSeconds,
        };
      }

      const token = body.token;
      const refresh_token = body.refresh_token;
      const userData = body.user;

      if (!token || !userData) {
        return {
          success: false,
          error: body?.error?.message || 'Invalid login response from server',
        };
      }

      const portalError = rejectWrongPortal(userData);
      if (portalError) {
        return { success: false, error: portalError };
      }

      persistSession({ token, refreshToken: refresh_token, user: userData });
      applySession({ token, user: userData });
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      const msg =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        'Invalid credentials. Please check your username and password.';
      return { success: false, error: msg };
    }
  };

  const verifyOtp = async ({ otpSessionId, otp }) => {
    try {
      const response = await csAdminApi.verifyCsadminOtp({ otpSessionId, otp });
      const body = response.data;
      const token = body.token;
      const refresh_token = body.refresh_token;
      const userData = body.user;

      if (!token || !userData) {
        return {
          success: false,
          error: body?.error?.message || 'Invalid OTP response from server',
        };
      }

      const portalError = rejectWrongPortal(userData);
      if (portalError) {
        return { success: false, error: portalError };
      }

      persistSession({ token, refreshToken: refresh_token, user: userData });
      applySession({ token, user: userData });
      return { success: true, user: userData };
    } catch (error) {
      console.error('OTP verification error:', error);
      const msg =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        'Invalid verification code';
      return { success: false, error: msg };
    }
  };

  const logout = async () => {
    try {
      await csAdminApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearCsadminAuthStorage();
      applySession(null);
    }
  };

  const updateUser = (userData) => {
    const normalized = normalizeUserForSession(userData);
    setUser(normalized);
    const storage = getActiveAuthStorage();
    localStorage.setItem(storage.user, JSON.stringify(normalized));
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    verifyOtp,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
